import { Table } from '@rd-web-markets/shared/dist/util';

import { SubmitButton, AddButton, CancelButton, DeleteButton, EditButton } from '@rd-web-markets/shared/dist/util/buttons';
import { extractMonthYear } from '@rd-web-markets/shared/dist/util/date/dateUtils';
import React, { useEffect, useRef, useState } from 'react';
import { Form, Popover } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import claimProjectReportService from '@services/claim_project_report.service';
import { REPORT_TYPE_INCLUDE_SECTION_COLUMN_NAME, SHOULD_PROJECT_POPOVER_EXCLUDE_OTHER_PROJECTS } from '@rd-web-markets/market/dist/constants';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useDispatch } from 'react-redux';
import claimGroupService from '@rd-web-markets/shared/dist/services/claim_group.service';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import claimProjectReportsListsService from '@rd-web-markets/shared/dist/services/lists/claim_groups/claim_project_reports_lists.service';
import Pagination from '@rd-web-markets/shared/dist/util/Pagination';
import { Loader } from '@rd-web-markets/shared/dist/util/Loader';

function SelectProjectsPopover({ claimGroup, width = '600px', onClose, accountType, ...props }, ref) {
  const { search } = useLocation(); //Required for Italy
  const reportType = new URLSearchParams(search).get('report_type') // returns 'inexpert_declaration_dossier' || 'technical' || 'expert_declaration_dossier'
  const history = useHistory();
  const dispatch = useDispatch();
  const cancelButtonText = process.env.PUBLIC_URL === '/it' ? 'close': undefined;
  const [projectsAreLoading, setProjectsAreLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectDrafts, setProjectDrafts] = useState({});
  const [renamedProjects, setRenamedProjects] = useState({});
  const [automaticallyExcludedProjects, setAutomaticallyExcludedProjects] = useState({});
  const [editingProjectIds, setEditingProjectIds] = useState({});
  const [newProjects, setNewProjects] = useState([]);
  const nextNewProjectId = useRef(0);
  const [reportUpdated, setReportUpdated] = useState(false);

  const [ projects, setProjects, totalPages, , , , fetchAllProjects] = claimProjectReportsListsService.useFetchAll({
    accountType,
    claimGroupId: claimGroup.id,
    setLoading: setProjectsAreLoading,
    groupProjects: false,
    fetchImmediatelly: false,
    updateUrl: false
  });

  useEffect(() => {
    fetchAllProjects({
      page: currentPage,
      order_by: 'project_name',
      fields: 'include_in_technical_report'
    });
  }, [currentPage, fetchAllProjects]);

  const projectsForCurrentPage = projects.map(project => ({
    ...project,
    ...projectDrafts[project.id],
    edit: !!editingProjectIds[project.id]
  }));
  const newProjectsForCurrentPage = newProjects.filter(project => project.createdOnPage === currentPage);
  const displayedProjects = [...projectsForCurrentPage, ...newProjectsForCurrentPage];

  const addProject = () => {
    const alreadyHasEmptyNewProject = newProjects.some(project => !project.project_name);

    if (alreadyHasEmptyNewProject) {
      return;
    }

    nextNewProjectId.current += 1;
    setNewProjects(currentProjects => [
      ...currentProjects,
      {
        clientId: nextNewProjectId.current,
        createdOnPage: currentPage,
        project_name: '',
        start_date: extractMonthYear(claimGroup.claims[0].from_date),
        end_date: extractMonthYear(claimGroup.claims[claimGroup.claims.length - 1].to_date),
        new: true,
      },
    ]);

    setReportUpdated(true)
  };

  const uncheckOtherReports = (marketSpecificReport, selectedProject) => {
    if (SHOULD_PROJECT_POPOVER_EXCLUDE_OTHER_PROJECTS && marketSpecificReport=== 'include_in_technical_report'){
      const existingProjectsToExclude = displayedProjects.filter(project =>
        !project.new &&
        project.id !== selectedProject.id &&
        project.include_in_technical_report
      );
      const newProjectsToExclude = displayedProjects.filter(project =>
        project.new &&
        project.clientId !== selectedProject.clientId &&
        project.include_in_technical_report
      );

      setProjectDrafts(currentDrafts => {
        const updatedDrafts = { ...currentDrafts };
        existingProjectsToExclude.forEach(project => {
          updatedDrafts[project.id] = {
            ...updatedDrafts[project.id],
            include_in_technical_report: false
          };
        });
        return updatedDrafts;
      });
      setAutomaticallyExcludedProjects(currentProjects => {
        const updatedProjects = { ...currentProjects };
        existingProjectsToExclude.forEach(project => {
          updatedProjects[project.id] = project.project_name;
        });
        return updatedProjects;
      });
      setNewProjects(currentProjects => currentProjects.map(project =>
        newProjectsToExclude.some(projectToExclude => projectToExclude.clientId === project.clientId)
          ? { ...project, include_in_technical_report: false }
          : project
      ));
    }
  }

  const removeProjectDraftField = (projectId, fieldName) => {
    setProjectDrafts(currentDrafts => {
      const updatedDrafts = { ...currentDrafts };
      const updatedProjectDraft = { ...updatedDrafts[projectId] };
      delete updatedProjectDraft[fieldName];

      if (Object.keys(updatedProjectDraft).length === 0) {
        delete updatedDrafts[projectId];
      } else {
        updatedDrafts[projectId] = updatedProjectDraft;
      }

      return updatedDrafts;
    });
  }

  const includeExcludeProject = async (event, project, marketSpecificReport) => {
    const isIncluded = event.target.checked;

    if (project.new) {
      setNewProjects(currentProjects => currentProjects.map(currentProject =>
        currentProject.clientId === project.clientId
          ? { ...currentProject, [marketSpecificReport]: isIncluded }
          : currentProject
      ));
      uncheckOtherReports(marketSpecificReport, project);
      setReportUpdated(true);
      return;
    }

    try {
      const persistedProject = projects.find(currentProject => currentProject.id === project.id);
      const updatedProject = await claimProjectReportService.update(claimGroup.id, {
        ...persistedProject,
        [marketSpecificReport]: isIncluded
      });

      setProjects(currentProjects => currentProjects.map(currentProject =>
        currentProject.id === updatedProject.id ? updatedProject : currentProject
      ));
      removeProjectDraftField(project.id, marketSpecificReport);
      setAutomaticallyExcludedProjects(currentProjects => {
        const updatedProjects = { ...currentProjects };
        delete updatedProjects[project.id];
        return updatedProjects;
      });
      uncheckOtherReports(marketSpecificReport, project);
      setReportUpdated(true);
    } catch(error) {
      dispatch(handleError(error))
    }
  }


  const includeProject = (projectReport) => {
    let marketSpecificReport = REPORT_TYPE_INCLUDE_SECTION_COLUMN_NAME[reportType || 'technical'];
    return (
      <div style={{ textAlign: 'center' }}>
        <Form.Check
          type="checkbox"
          name="project_included"
          className="pe-5"
          checked={projectReport[marketSpecificReport]}
          value={projectReport[marketSpecificReport]}
          disabled={false}
          onChange={(event) => includeExcludeProject(event, projectReport, marketSpecificReport)}
        />
      </div>
    );
  };

  const setProjectReportToEditted = (projectReport) => {
    setEditingProjectIds(currentProjectIds => ({
      ...currentProjectIds,
      [projectReport.id]: true
    }));
  };

  const updateProjectName = (projectReport, projectName) => {
    if (projectReport.new) {
      setNewProjects(currentProjects => currentProjects.map(currentProject =>
        currentProject.clientId === projectReport.clientId
          ? { ...currentProject, project_name: projectName }
          : currentProject
      ));
      setReportUpdated(true);
      return;
    }

    const persistedProject = projects.find(project => project.id === projectReport.id);
    const originalProjectName = renamedProjects[projectReport.id]?.originalProjectName || persistedProject.project_name;

    setProjectDrafts(currentDrafts => {
      const updatedDrafts = { ...currentDrafts };
      const updatedProjectDraft = { ...updatedDrafts[projectReport.id] };

      if (projectName === originalProjectName) {
        delete updatedProjectDraft.project_name;
      } else {
        updatedProjectDraft.project_name = projectName;
      }

      if (Object.keys(updatedProjectDraft).length === 0) {
        delete updatedDrafts[projectReport.id];
      } else {
        updatedDrafts[projectReport.id] = updatedProjectDraft;
      }

      return updatedDrafts;
    });
    setRenamedProjects(currentProjects => {
      const updatedProjects = { ...currentProjects };

      if (projectName === originalProjectName) {
        delete updatedProjects[projectReport.id];
      } else {
        updatedProjects[projectReport.id] = {
          originalProjectName,
          projectName
        };
      }

      return updatedProjects;
    });
    setReportUpdated(true);
  }

  const projectName = (projectReport) => {
    if (projectReport.new || projectReport.edit) {
      return (
        <span className='d-flex'>
          <Form.Control
            value={projectReport.project_name}
            name='project_name'
            onChange={(event) => updateProjectName(projectReport, event.target.value)}
          />
        </span>
      );
    }

    return (
      <span>
        <span className='me-1'>{projectReport.project_name}</span>
        <EditButton title='Edit project name' onClick={() => setProjectReportToEditted(projectReport)} />
      </span>
    );
  };

  const rows = displayedProjects.map(projectReport => ({
    key: projectReport.id || `new-project-${projectReport.clientId}`,
    data: { project: projectReport },
    columns: [projectName(projectReport), includeProject(projectReport)],
    showDelete: true,
    showEdit: !projectReport.new,
  }));

  const deleteNewProject = async (index, row) => {
    const { project } = row.data;

    if (project.new) {
      setNewProjects(currentProjects =>
        currentProjects.filter(currentProject => currentProject.clientId !== project.clientId)
      );
    } else {
      await claimProjectReportService.delete(project.claim_group_id, project.id);
      window.location.reload();
    }
  };

  const headers = [{ text: 'Project Name' }, { text: 'Include in report' }];

  const enableSubmit = () => {
    let marketSpecificReport = REPORT_TYPE_INCLUDE_SECTION_COLUMN_NAME[reportType];
    return displayedProjects.some(project => project[marketSpecificReport] === true)
  }

  const saveProjects = useErrorHandling(async () => {
    const existingProjectDrafts = Object.entries(projectDrafts).map(([id, projectDraft]) => ({
      id: Number(id),
      ...projectDraft
    }));
    const newProjectDrafts = newProjects.map(project => {
      const newProjectDraft = { ...project };
      delete newProjectDraft.clientId;
      delete newProjectDraft.createdOnPage;
      delete newProjectDraft.new;
      delete newProjectDraft.edit;

      return newProjectDraft;
    });
    const projectsToSave = [...existingProjectDrafts, ...newProjectDrafts];

    if (projectsToSave.length === 0) return;

    await claimGroupService.update({
      ...claimGroup,
      projects: projectsToSave,
      claim_project_reports_attributes: projectsToSave
    })
  })

  const renamedProjectDescriptions = Object.values(renamedProjects).map(project =>
    `${project.originalProjectName} → ${project.projectName}`
  );
  const automaticallyExcludedProjectNames = Object.entries(automaticallyExcludedProjects).map(
    ([projectId, projectName]) => renamedProjects[projectId]?.projectName || projectName
  );
  const hasPendingChanges =
    newProjects.length > 0 ||
    Object.keys(projectDrafts).length > 0;

  return (
    <div>
      <style>
        {`
          .popover-custom {
            max-width: ${width};
            width: ${width};
          }
        `}
      </style>
      <Popover bsPrefix='popover popover-custom' {...props} ref={ref}>
        <Popover.Body style={{ fontSize: '0.8rem' }}>
          {hasPendingChanges &&
            <ul>
              {newProjects.length > 0 &&
                <li>New projects after save: {newProjects.map(project => project.project_name).join(', ')}</li>
              }
              {renamedProjectDescriptions.length > 0 &&
                <li>Renamed projects after save: {renamedProjectDescriptions.join(', ')}</li>
              }
              {automaticallyExcludedProjectNames.length > 0 &&
                <li>Projects excluded after save: {automaticallyExcludedProjectNames.join(', ')}</li>
              }
            </ul>
          }
          <h5 className='text-center mt-3'>Select which projects to include in your report</h5>
          <hr />

          {projectsAreLoading ? <Loader /> :
            <Table
              style={{ width: '100%' }}
              opts={{ headersClass: 'text-primary text-end' }}
              headers={headers}
              rows={rows}
              showControls={true}
              onDelete={deleteNewProject}
              onEdit={(index, row) =>
                history.push(`/${accountType}/claim_groups/${claimGroup.id}/technical_proof/project_reports/${row.data.project.id}`)
              }
            />
          }
          {totalPages > 1 &&
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={setCurrentPage}
            />
          }
          {process.env.PUBLIC_URL !== '/it' &&
            <div className='mb-3'>
              <AddButton text='Add Project' onClick={addProject} />
            </div>
          }

          <div style={{ width: 'fit-content', margin: '0 auto' }}>
            <CancelButton className='me-1' onClick={onClose} text={cancelButtonText}/>
            <SubmitButton
              text = { reportUpdated ? 'Save*' : 'Save'}
              onClick={async () => {
                if (reportUpdated) {
                  if (hasPendingChanges) {
                    await saveProjects()
                  }
                  window.location.reload()
                } else {
                  onClose()
                }
              }}
            />
          </div>
        </Popover.Body>
      </Popover>
    </div>
  );
}

export default React.forwardRef(SelectProjectsPopover);
